import { invalidateMenuCache, saveMenuTree } from '@/utils/menuCache.mjs';
import axios from 'axios';
import {
    empty,
    getToken,
    isStandaloneCustomer,
    shouldRefreshCurrentAccessToken,
    touchTokenExpiry
} from '@/utils/auth';
import { API_CODE } from '@/constants/http';
import { STORAGE_KEYS } from '@/constants/app';
import { env } from '@/config/env';
import { t } from '@/utils/i18nT';
import { clearPersistentToken } from '@/utils/persistentAuth';
import { runExclusiveSilentLogin, setSyncedToken } from '@/utils/authSync';
import {
    ADMIN_WEB_DEVICE,
    createSingleFlight,
    joinApiUrl,
    runWithNetworkRetry,
    SILENT_LOGIN_REQUEST_TIMEOUT_MS,
    SILENT_LOGIN_TYPE
} from '@/utils/authSession.mjs';

const REQUEST_TIMEOUT_MS = SILENT_LOGIN_REQUEST_TIMEOUT_MS;

let authSwitchMenuSyncPromise = null;

export function getRequestLang() {
    let lang = window.localStorage.getItem(STORAGE_KEYS.lang);
    if (empty(lang)) {
        lang = 'cn';
    }
    return lang;
}

export function persistLoginInfo(data) {
    const info = data?.info;
    if (!info) {
        return;
    }
    if (info.authid != null && info.authid !== '') {
        window.localStorage.setItem('authid', String(info.authid));
    } else {
        window.localStorage.removeItem('authid');
    }
    window.localStorage.setItem('replenisher_stock_only', info.replenisher_stock_only ? '1' : '0');
    window.localStorage.setItem("isMain", info.isMain);
                    window.localStorage.setItem('isStandaloneCustomer', info.isStandaloneCustomer ? '1' : '0');
    window.localStorage.setItem("ismain", info.ismain != null ? String(info.ismain) : "0");
    window.localStorage.setItem("macBigTypeIds", info.macBigTypeIds);
    window.localStorage.setItem("oid", info.oid);
    window.localStorage.setItem("moid", info.moid);
    window.localStorage.setItem("lifeunit", info.lifeunit);
    window.localStorage.setItem("isinputlife", info.isinputlife);
    window.localStorage.setItem("ordermark1name", info.ordermark1name);
    window.localStorage.setItem("ordermark2name", info.ordermark2name);
    window.localStorage.setItem("ordermark3name", info.ordermark3name);
    window.localStorage.setItem("odmark1name", info.odmark1name);
    window.localStorage.setItem("odmark2name", info.odmark2name);
    window.localStorage.setItem("odmark3name", info.odmark3name);
    window.localStorage.setItem("outodmark1name", info.outodmark1name || "");
    window.localStorage.setItem("outodmark2name", info.outodmark2name || "");
    window.localStorage.setItem("outodmark3name", info.outodmark3name || "");
    window.localStorage.setItem("rordermark1name", info.rordermark1name);
    window.localStorage.setItem("rordermark2name", info.rordermark2name);
    window.localStorage.setItem("rordermark3name", info.rordermark3name);
    window.localStorage.setItem("rodmark1name", info.rodmark1name);
    window.localStorage.setItem("rodmark2name", info.rodmark2name);
    window.localStorage.setItem("rodmark3name", info.rodmark3name);
    window.localStorage.setItem("bordermark1name", info.bordermark1name);
    window.localStorage.setItem("bordermark2name", info.bordermark2name);
    window.localStorage.setItem("bordermark3name", info.bordermark3name);
    window.localStorage.setItem("brordermark1name", info.brordermark1name);
    window.localStorage.setItem("brordermark2name", info.brordermark2name);
    window.localStorage.setItem("brordermark3name", info.brordermark3name);
    sessionStorage.setItem(STORAGE_KEYS.sessionFilesUrl, data.filesUrl || '');
    sessionStorage.setItem(STORAGE_KEYS.sessionUsername, info.username || '');
    sessionStorage.setItem(STORAGE_KEYS.sessionHeadimgurl, info.headimgurl || '');
    if (info.userid != null && info.userid !== '') {
        sessionStorage.setItem(STORAGE_KEYS.sessionUserid, String(info.userid));
        window.localStorage.setItem('userid', String(info.userid));
    }
}

export function ensureRouterMenusAfterAuthSwitch() {
    if (authSwitchMenuSyncPromise) {
        return authSwitchMenuSyncPromise;
    }
    const token = window.sessionStorage.getItem(STORAGE_KEYS.token);
    if (empty(token)) {
        return Promise.resolve();
    }
    invalidateMenuCache();
    authSwitchMenuSyncPromise = axios({
        url: joinApiUrl(env.BASE_API, '/adminapi/functionListMe'),
        method: 'post',
        timeout: REQUEST_TIMEOUT_MS,
        headers: {
            token,
            lang: getRequestLang(),
        },
    })
        .then((resp) => {
            const body = resp?.data;
            if (body && body.code === API_CODE.OK && Array.isArray(body.data)
                && token === window.sessionStorage.getItem(STORAGE_KEYS.token)) {
                touchTokenExpiry();
                saveMenuTree(body.data);
            } else if (isStandaloneCustomer()) {
                throw new Error(t('loginSessionExpired'));
            }
        })
        .catch((error) => {
            if (isStandaloneCustomer()) {
                saveMenuTree([]);
                throw error;
            }
            // Menu sync is best effort; auth refresh has already succeeded.
        })
        .finally(() => {
            authSwitchMenuSyncPromise = null;
        });
    return authSwitchMenuSyncPromise;
}

export function completeAuthSwitch(data) {
    if (empty(data?.token)) {
        throw new Error(t('loginSessionExpired'));
    }
    setSyncedToken(data.token, data?.info?.userid);
    persistLoginInfo(data);
    return ensureRouterMenusAfterAuthSwitch().then(() => data);
}

function completeSilentLogin(resp) {
    const body = resp?.data;
    if (!body || ![API_CODE.OK, API_CODE.AUTH_SWITCH].includes(body.code)) {
        if (body?.code === API_CODE.PTOKEN_INVALID || body?.code === API_CODE.NOT_ADMIN) {
            clearPersistentToken();
        }
        throw new Error(body?.msg || t('loginSessionExpired'));
    }
    return completeAuthSwitch(body.data);
}

function cookieSilentLogin() {
    return runWithNetworkRetry(() => axios({
            url: joinApiUrl(env.BASE_API, '/adminapi/Login'),
            method: 'post',
            timeout: REQUEST_TIMEOUT_MS,
            withCredentials: true,
            headers: {
                lang: getRequestLang(),
            },
            data: {
                device: ADMIN_WEB_DEVICE,
                loginType: SILENT_LOGIN_TYPE,
            },
        }))
        .then(completeSilentLogin);
}

const runSingleSilentLogin = createSingleFlight(
    () => runExclusiveSilentLogin(cookieSilentLogin)
);

export function silentLogin() {
    return runSingleSilentLogin();
}

export async function refreshStandaloneAuthority() {
    if (window.localStorage.getItem('isStandaloneCustomer') !== '1' && window.__AUTOMACHINE_DEPLOYMENT__?.CUSTOMER_PROFILE !== true) return;
    const token = getToken();
    const response = await axios.get(joinApiUrl(env.BASE_API, '/adminapi/checkToken'), { params: { include_identity: 1 }, headers: { token }, timeout: REQUEST_TIMEOUT_MS });
    if (response.data?.code !== API_CODE.OK || !response.data.data?.info) throw new Error(t('loginSessionExpired'));
    persistLoginInfo({ info: response.data.data.info, filesUrl: sessionStorage.getItem(STORAGE_KEYS.sessionFilesUrl) });
    await ensureRouterMenusAfterAuthSwitch();
}

export function ensureFreshAccessToken() {
    const token = getToken();
    if (!empty(token) && !shouldRefreshCurrentAccessToken()) {
        return Promise.resolve({ token, refreshed: false });
    }
    return silentLogin();
}

export function setupAccessTokenRefreshLifecycle(options = {}) {
    let refreshPromise = null;
    const onRefreshFailure = typeof options.onRefreshFailure === 'function'
        ? options.onRefreshFailure
        : () => {};

    const refreshWhenVisible = () => {
        if (document.visibilityState === 'hidden'
            || window.location.pathname === '/'
            || empty(getToken())
            || !shouldRefreshCurrentAccessToken()
            || refreshPromise) {
            return;
        }
        refreshPromise = ensureFreshAccessToken()
            .catch((error) => onRefreshFailure(error))
            .finally(() => {
                refreshPromise = null;
            });
    };

    const onVisibilityChange = () => {
        if (document.visibilityState !== 'hidden') {
            refreshWhenVisible();
        }
    };

    window.addEventListener('pageshow', refreshWhenVisible);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
        window.removeEventListener('pageshow', refreshWhenVisible);
        document.removeEventListener('visibilitychange', onVisibilityChange);
    };
}
